import { Link } from 'react-router';
import type { Api, CandidateRow } from '../lib/api';
import { categoryLabel, formatRange } from '../lib/format';
import { CandidateActions, CandidateStatusText, OutcomeBadge } from './imports';

/**
 * Tabella dei candidati (coda di revisione e risultati di un'esecuzione), con selezione per le
 * azioni in blocco e le azioni rapide per riga.
 */
export function CandidateTable({
  rows,
  api,
  selected,
  onSelect,
  onDone,
  sourceName,
}: {
  rows: CandidateRow[];
  api: Api;
  selected?: Set<string>;
  onSelect?: (ids: string[], on: boolean) => void;
  onDone: (row: CandidateRow, eventId?: string) => void;
  sourceName?: (id: string) => string;
}) {
  const selectable = rows.filter((r) => r.status === 'needs_review');
  const allOn = selectable.length > 0 && selectable.every((r) => selected?.has(r.id));
  return (
    <div className="relative overflow-x-auto">
      <table className="w-full min-w-[980px] border-collapse text-sm">
        <thead>
          <tr className="border-b-2 border-fg text-left text-label uppercase tracking-[0.04em] text-muted">
            {onSelect && (
              <th className="w-8 py-2 pr-2">
                <input
                  type="checkbox"
                  aria-label="Seleziona tutti"
                  checked={allOn}
                  onChange={(e) =>
                    onSelect(
                      selectable.map((r) => r.id),
                      e.target.checked,
                    )
                  }
                />
              </th>
            )}
            <th className="py-2 pr-3 font-medium">Date</th>
            <th className="py-2 pr-3 font-medium">Evento letto</th>
            <th className="py-2 pr-3 font-medium">Esito</th>
            <th className="py-2 pr-3 font-medium">Evento simile</th>
            <th className="py-2 font-medium">
              <span className="sr-only">Azioni</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} className="border-b border-surface-strong align-top hover:bg-surface">
              {onSelect && (
                <td className="py-2 pr-2">
                  {r.status === 'needs_review' && (
                    <input
                      type="checkbox"
                      aria-label={`Seleziona ${r.title}`}
                      checked={selected?.has(r.id) ?? false}
                      onChange={(e) => onSelect([r.id], e.target.checked)}
                    />
                  )}
                </td>
              )}
              <td className="py-2 pr-3 whitespace-nowrap">{formatRange(r.startDate, r.endDate)}</td>
              <td className="py-2 pr-3">
                <Link to={`/review/${r.id}`} className="font-medium hover:underline">
                  {r.title}
                </Link>
                <div className="mt-0.5 flex flex-wrap gap-x-2 text-xs text-muted">
                  <span>{r.frazione ? `${r.frazione}, ${r.comune}` : r.comune}</span>
                  <span>{categoryLabel(r.category)}</span>
                  {sourceName && <span>{sourceName(r.sourceId)}</span>}
                  {r.issues > 0 && <span title="Avvisi della validazione">⚠ {r.issues}</span>}
                  {r.outcome === 'new' && !r.enriched && r.status === 'needs_review' && (
                    <span title="I testi riscritti con l'AI arrivano poco dopo la lettura">
                      testi in arrivo
                    </span>
                  )}
                </div>
              </td>
              <td className="py-2 pr-3">
                <OutcomeBadge outcome={r.outcome} />
                <div>
                  <CandidateStatusText row={r} />
                </div>
              </td>
              <td className="py-2 pr-3">
                {r.match ? (
                  <>
                    {r.match.kind === 'event' ? (
                      <Link to={`/events/${r.match.id}`} className="hover:underline">
                        {r.match.title}
                      </Link>
                    ) : (
                      <Link to={`/review/${r.match.id}`} className="hover:underline">
                        {r.match.title} <span className="text-xs text-muted">(da rivedere)</span>
                      </Link>
                    )}
                    <div className="text-xs text-muted">
                      somiglianza {r.match.score.toFixed(2).replace('.', ',')}
                    </div>
                  </>
                ) : (
                  <span className="text-xs text-muted">—</span>
                )}
              </td>
              <td className="py-2">
                <CandidateActions row={r} api={api} onDone={onDone} />
              </td>
            </tr>
          ))}
          {rows.length === 0 && (
            <tr>
              <td colSpan={6} className="py-4 text-muted">
                Niente da mostrare.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
